import { inject, Injectable } from '@angular/core';
import { Angulartics2 } from 'angulartics2';
import { UserService } from '@app/core/services/user.service';
import { Company, User } from '@app/generated/graphql';
import {
  INotificationBaseContext,
  IAlertBaseContext,
  INotificationAlertCenterOpenedContext,
  INotificationAlertCenterClosedContext,
  INotificationClickedContext,
  IAlertClickedContext,
  INotificationDismissedContext,
  INotificationCtaContext,
  INotificationToastContext,
  IAlertToastContext,
  IAlertCtaContext,
  IAlertDismissedContext,
  INotificationAlertBatchActionContext,
  INotificationAlertHistoryViewedContext,
  INotificationAlertHistoryFilteredContext,
  IPreferencesViewedContext,
  IChannelUpdatedContext,
  IEmailCtaContext,
} from '../interfaces/analytics.interface';

@Injectable({
  providedIn: 'root',
})
export class NotificationPreferencesAnalyticsService {
  private _angulartics2 = inject(Angulartics2);
  private _userService = inject(UserService);

  private _track(
    eventName: string,
    properties: Record<string, any> = {},
  ): void {
    const user: User = this._userService.getUserValue();
    const company: Company = this._userService.getCompanyValue();
    const timestamp: string = new Date().toISOString();

    this._angulartics2.eventTrack.next({
      action: eventName,
      properties: {
        user_id: user?.id,
        company_id: company?.id,
        timestamp,
        ...properties,
      },
    });
  }

  private notificationProperties(
    context: INotificationBaseContext,
  ): Record<string, any> {
    return {
      notification_id: context.notification?.id,
      notification_name: context.notification?.name,
      notification_description: context.notification?.description,
      notification_slug: context.notification?.notificationSlug,
      notification_severity: context.notification?.severity,
      source: context?.source ?? '',
    };
  }

  private alertProperties(context: IAlertBaseContext): Record<string, any> {
    return {
      alert_id: context.alert?.id,
      alert_name: context.alert?.name,
      alert_description: context.alert?.description,
      alert_slug: context.alert?.notificationSlug,
      alert_severity: context.alert?.severity,
      source: context?.source ?? '',
    };
  }

  trackNotificationCenterOpened(
    context: INotificationAlertCenterOpenedContext,
  ): void {
    this._track('notification_center_opened', {
      unread_count: context.unreadCount,
    });
  }

  trackNotificationCenterClosed(
    context: INotificationAlertCenterClosedContext,
  ): void {
    this._track('notification_center_closed', {
      time_open_seconds: context.timeOpenSeconds,
    });
  }

  trackNotificationClicked(context: INotificationClickedContext): void {
    this._track('notification_clicked', {
      ...this.notificationProperties(context),
      time_since_received_seconds: context.timeSinceReceivedSeconds,
    });
  }

  trackNotificationDismissed(context: INotificationDismissedContext): void {
    this._track('notification_dismissed', {
      ...this.notificationProperties(context),
      time_since_displayed_seconds: context.timeSinceDisplayedSeconds,
    });
  }

  trackNotificationCtaClicked(context: INotificationCtaContext): void {
    this._track('notification_cta_clicked', {
      ...this.notificationProperties(context),
      cta_label: context.ctaLabel,
      cta_destination: context.ctaDestination,
    });
  }

  trackNotificationsBatchAction(
    context: INotificationAlertBatchActionContext,
  ): void {
    this._track('notifications_batch_action', {
      action_type: context.actionType,
      notifications_affected: context.affectedCount,
    });
  }

  trackNotificationHistoryViewed(
    context: INotificationAlertHistoryViewedContext,
  ): void {
    this._track('notification_history_viewed', {
      total_notifications: context.totalCount,
      unread_notifications: context.unreadCount,
    });
  }

  trackNotificationHistoryFiltered(
    context: INotificationAlertHistoryFilteredContext,
  ): void {
    this._track('notification_history_filtered', {
      filter_value: context.filterValue,
      results_count: context.resultsCount,
    });
  }

  trackToastDisplayed(context: INotificationToastContext): void {
    this._track('notification_toast_displayed', {
      ...this.notificationProperties(context),
    });
  }

  trackToastClicked(context: INotificationToastContext): void {
    this._track('notification_toast_clicked', {
      ...this.notificationProperties(context),
      time_displayed_seconds: context.timeDisplayedSeconds,
    });
  }

  trackNotificationToastExpired(context: INotificationToastContext): void {
    this._track('notification_toast_expired', {
      ...this.notificationProperties(context),
      time_displayed_seconds: context.timeDisplayedSeconds,
      display_duration_seconds: context.displayDurationSeconds,
    });
  }

  trackAlertCenterOpened(context: INotificationAlertCenterOpenedContext): void {
    this._track('alert_center_opened', {
      unread_count: context.unreadCount,
    });
  }

  trackAlertToastClicked(context: IAlertToastContext): void {
    this._track('alert_toast_clicked', {
      ...this.alertProperties(context),
      time_displayed_seconds: context.timeDisplayedSeconds,
    });
  }

  trackAlertCenterClosed(context: INotificationAlertCenterClosedContext): void {
    this._track('alert_center_closed', {
      time_open_seconds: context.timeOpenSeconds,
    });
  }

  trackAlertClicked(context: IAlertClickedContext): void {
    this._track('alert_clicked', {
      ...this.alertProperties(context),
      time_since_received_seconds: context.timeSinceReceivedSeconds,
    });
  }

  trackAlertDismissed(context: IAlertDismissedContext): void {
    this._track('alert_dismissed', {
      ...this.alertProperties(context),
      time_since_displayed_seconds: context.timeSinceDisplayedSeconds,
    });
  }

  trackAlertCtaClicked(context: IAlertCtaContext): void {
    this._track('alert_cta_clicked', {
      ...this.alertProperties(context),
      cta_label: context.ctaLabel,
      cta_destination: context.ctaDestination,
      time_since_displayed_seconds: context.timeSinceDisplayedSeconds,
    });
  }

  trackAlertsBatchAction(context: INotificationAlertBatchActionContext): void {
    this._track('alerts_batch_action', {
      action_type: context.actionType,
      affected_count: context.affectedCount,
    });
  }

  trackAlertHistoryViewed(
    context: INotificationAlertHistoryViewedContext,
  ): void {
    this._track('alert_history_viewed', {
      total_count: context.totalCount,
      unread_count: context.unreadCount,
    });
  }

  trackAlertHistoryFiltered(
    context: INotificationAlertHistoryFilteredContext,
  ): void {
    this._track('alert_history_filtered', {
      filter_value: context.filterValue,
      results_count: context.resultsCount,
    });
  }

  trackAlertToastExpired(context: IAlertToastContext): void {
    this._track('alert_toast_expired', {
      ...this.alertProperties(context),
      time_displayed_seconds: context.timeDisplayedSeconds,
      display_duration_seconds: context.displayDurationSeconds,
    });
  }

  trackPreferencesViewed(context: IPreferencesViewedContext): void {
    this._track('notification_preferences_viewed', {
      current_channel_count: context.currentChannelCount,
    });
  }

  trackChannelUpdated(context: IChannelUpdatedContext): void {
    this._track('notification_channel_updated', {
      channel: context.channel,
      new_state: context.newState,
    });
  }

  trackEmailCtaClicked(context: IEmailCtaContext): void {
    this._track('notification_email_cta_clicked', {
      ...this.notificationProperties(context),
      cta_label: context.ctaLabel,
      cta_destination: context.ctaDestination,
    });
  }
}
